import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineSpinnerIcon from 'LuxKit/Icons/line/LineSpinnerIcon'

type RecordingOverlaySize = 'standard' | 'large'

interface RecordingOverlayProps {
  placeholder?: string
  waveformLevels: Array<number>
  filledBars: number
  durationMs: number
  onCancel: () => void
  onSubmit: () => void
  size?: RecordingOverlaySize
  isTranscribing?: boolean
}

const Overlay = styled.div<{ $size: RecordingOverlaySize }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  padding: ${props =>
    props.$size === 'large'
      ? `${rem(20)} ${rem(28)}`
      : `${rem(12)} ${rem(18)}`};
  background: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${rem(20)};
  border: none;
  box-shadow: none;
  color: ${props => props.theme.palette.neutral.default.one};
  z-index: 1;
  pointer-events: auto;
`

const Waveform = styled.div<{ $size: RecordingOverlaySize }>`
  flex: 1;
  height: ${props => props.$size === 'large' ? rem(48) : rem(36)};
  display: flex;
  align-items: center;
  gap: ${rem(2)};
  overflow: hidden;
  position: relative;
`

const WaveformBar = styled.span<{ $height: number; $active: boolean }>`
  width: ${rem(3)};
  border-radius: ${rem(999)};
  background: ${props => props.$active ? props.theme.palette.neutral.default.one : props.theme.palette.neutral.default.five};
  opacity: ${props => props.$active ? 1 : 0.65};
  height: ${props => `${props.$height}%`};
  flex: 0 0 ${rem(3)};
  transform-origin: center;
  transition: height 0.12s ease;
`

const CircleButton = styled.button<{ $accent?: boolean }>`
  width: ${rem(36)};
  height: ${rem(36)};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${props => props.$accent ? props.theme.palette.brand.primary.normal : props.theme.palette.neutral.default.five};
  background: ${props => props.$accent ? props.theme.palette.brand.primary.normal : props.theme.palette.neutral.default.eight};
  color: ${props => props.$accent ? '#fff' : props.theme.palette.neutral.default.one};
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: ${props => props.$accent ? props.theme.palette.brand.primary.hover : props.theme.palette.neutral.default.four};
    background: ${props => props.$accent ? props.theme.palette.brand.primary.hover : props.theme.palette.neutral.default.eight};
  }

  &:active {
    transform: scale(0.96);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const SpinnerIcon = styled(LineSpinnerIcon)`
  animation: ${spin} 0.9s linear infinite;
`

const Timer = styled.span`
  font-size: ${rem(14)};
  font-variant-numeric: tabular-nums;
  color: ${props => props.theme.palette.neutral.default.two};
`

function formatDuration(durationMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

const MIN_BAR_HEIGHT = 18
const MAX_BAR_HEIGHT = 100
const MIN_RENDER_BARS = 24
const MAX_RENDER_BARS = 320
const BAR_WIDTH_PX = 3
const BAR_GAP_PX = 1.75
const STANDARD_BAR_SLOT_PX = BAR_WIDTH_PX + BAR_GAP_PX
const LARGE_BAR_SLOT_PX = BAR_WIDTH_PX + BAR_GAP_PX

function RecordingOverlay({
  placeholder,
  waveformLevels,
  filledBars,
  durationMs,
  onCancel,
  onSubmit,
  size = 'standard',
  isTranscribing = false,
}: RecordingOverlayProps) {
  const intl = useIntl()
  const label = placeholder || intl.formatMessage({
    id: 'ClYVDeW_rCg_lEoDxc1S',
    defaultMessage: 'Listening...',
    description: 'Fallback aria-label shown during voice recording when no placeholder is provided',
  })
  const [renderedBarCount, setRenderedBarCount] = useState(MIN_RENDER_BARS)
  const waveformRef = useRef<HTMLDivElement | null>(null)

  const updateRenderedBars = useCallback((width: number) => {
    if (!width) {
      return
    }
    const slot = size === 'large' ? LARGE_BAR_SLOT_PX : STANDARD_BAR_SLOT_PX
    const desiredCount = Math.max(
      MIN_RENDER_BARS,
      Math.min(MAX_RENDER_BARS, Math.floor((width + BAR_GAP_PX) / slot)),
    )
    setRenderedBarCount(prev => (prev === desiredCount ? prev : desiredCount))
  }, [size])

  useEffect(() => {
    const element = waveformRef.current
    if (!element || typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      const width = entry?.contentRect.width ?? 0
      updateRenderedBars(width)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [size, updateRenderedBars])

  useEffect(() => {
    const handleWindowResize = () => {
      if (!waveformRef.current) {
        return
      }
      updateRenderedBars(waveformRef.current.clientWidth)
    }
    handleWindowResize()
    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [updateRenderedBars])

  useEffect(() => {
    if (!waveformRef.current) {
      return
    }
    updateRenderedBars(waveformRef.current.clientWidth)
  }, [updateRenderedBars, waveformLevels.length])

  const formattedDuration = useMemo(() => formatDuration(durationMs), [durationMs])

  const adjustedWaveformLevels = useMemo(() => {
    if (renderedBarCount === waveformLevels.length) {
      return waveformLevels
    }
    if (renderedBarCount <= 0 || waveformLevels.length === 0) {
      return []
    }
    const result = Array.from({ length: renderedBarCount })
    for (let index = 0; index < renderedBarCount; index += 1) {
      const sourceIndex = Math.min(
        waveformLevels.length - 1,
        Math.floor((index / renderedBarCount) * waveformLevels.length),
      )
      result[index] = waveformLevels[sourceIndex]
    }
    return result
  }, [renderedBarCount, waveformLevels])

  const firstActiveIndex = useMemo(() => {
    if (renderedBarCount === 0) {
      return 0
    }
    const activeCount = Math.min(
      renderedBarCount,
      Math.max(
        0,
        Math.round((filledBars / Math.max(1, waveformLevels.length)) * renderedBarCount),
      ),
    )
    return Math.max(0, renderedBarCount - activeCount)
  }, [filledBars, renderedBarCount, waveformLevels.length])

  const waveformBars = useMemo(() => adjustedWaveformLevels.map((level, index) => {
    const height = Math.min(
      MAX_BAR_HEIGHT,
      Math.max(MIN_BAR_HEIGHT, MIN_BAR_HEIGHT + Number(level) * (MAX_BAR_HEIGHT - MIN_BAR_HEIGHT)),
    )
    const isActive = index >= firstActiveIndex
    return <WaveformBar key={`wave-${index}`} $height={height} $active={isActive} />
  }), [adjustedWaveformLevels, firstActiveIndex])

  return (
    <Overlay $size={size} role="status" aria-live="polite" aria-label={label}>
      <CircleButton
        type="button"
        aria-label={intl.formatMessage({
          id: '42RE6uT3_lz0oE0LIAEA',
          defaultMessage: 'Cancel recording',
          description: 'Aria-label for the cancel recording button during voice input',
        })}
        onClick={onCancel}
        disabled={isTranscribing}
      >
        <LineTimesIcon />
      </CircleButton>
      <Waveform aria-hidden $size={size} ref={waveformRef}>
        {waveformBars}
      </Waveform>
      <Timer>{formattedDuration}</Timer>
      <CircleButton
        type="button"
        aria-label={intl.formatMessage({
          id: '6lbT8mboL5lraJ8wKaPM',
          defaultMessage: 'Stop recording and transcribe',
          description: 'Aria-label for the stop recording and transcribe button during voice input',
        })}
        onClick={onSubmit}
        $accent
        disabled={isTranscribing}
      >
        {isTranscribing ? <SpinnerIcon /> : <LineCheckIcon />}
      </CircleButton>
    </Overlay>
  )
}

export default RecordingOverlay
